import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { API, formatApiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-rw-border bg-rw-bg px-4 py-3 text-sm text-rw-text placeholder:text-rw-dim focus:border-rw-green focus:outline-none transition-colors";
const labelCls = "block text-xs font-semibold uppercase tracking-wider text-rw-muted mb-1.5";

export function LeadForm({ source = "contact", showBudget = false, testidPrefix = "lead-form" }) {
  const [form, setForm] = useState({ name: "", business: "", website: "", email: "", phone: "", budget: "", message: "", fax: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (form.phone.replace(/\D/g, "").length < 6) e.phone = "Please enter a valid phone/WhatsApp number";
    if (form.message.trim().length < 5) e.message = "Tell us briefly what you want to improve";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await API.post("/leads", { ...form, source });
      setStatus("done");
      toast.success("Enquiry sent. We will get back to you shortly.");
    } catch (err) {
      setStatus("idle");
      toast.error(formatApiError(err));
    }
  };

  if (status === "done") {
    return (
      <div className="rounded-2xl border border-rw-green/40 bg-rw-card p-8 text-center" data-testid={`${testidPrefix}-success`}>
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-rw-green/15">
          <Send className="text-rw-green" size={24} />
        </div>
        <h3 className="font-jakarta text-xl font-bold text-rw-text">Enquiry received.</h3>
        <p className="mt-2 text-sm text-rw-muted">Thank you, {form.name.split(" ")[0]}. We will review your website and reply within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="space-y-4" data-testid={testidPrefix}>
      <input type="text" value={form.fax} onChange={set("fax")} tabIndex={-1} autoComplete="off"
        className="hidden" aria-hidden="true" data-testid={`${testidPrefix}-honeypot`} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${testidPrefix}-name`} className={labelCls}>Name *</label>
          <input id={`${testidPrefix}-name`} data-testid={`${testidPrefix}-name-input`} className={inputCls} value={form.name} onChange={set("name")} placeholder="Your name" />
          {errors.name && <p className="mt-1 text-xs text-red-400" role="alert">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-business`} className={labelCls}>Business Name</label>
          <input id={`${testidPrefix}-business`} data-testid={`${testidPrefix}-business-input`} className={inputCls} value={form.business} onChange={set("business")} placeholder="Your business" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor={`${testidPrefix}-website`} className={labelCls}>Website</label>
          <input id={`${testidPrefix}-website`} data-testid={`${testidPrefix}-website-input`} className={inputCls} value={form.website} onChange={set("website")} placeholder="https://yourwebsite.com" />
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-email`} className={labelCls}>Email *</label>
          <input id={`${testidPrefix}-email`} type="email" data-testid={`${testidPrefix}-email-input`} className={inputCls} value={form.email} onChange={set("email")} placeholder="you@company.com" />
          {errors.email && <p className="mt-1 text-xs text-red-400" role="alert">{errors.email}</p>}
        </div>
        <div>
          <label htmlFor={`${testidPrefix}-phone`} className={labelCls}>Phone / WhatsApp *</label>
          <input id={`${testidPrefix}-phone`} type="tel" data-testid={`${testidPrefix}-phone-input`} className={inputCls} value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
          {errors.phone && <p className="mt-1 text-xs text-red-400" role="alert">{errors.phone}</p>}
        </div>
        {showBudget && (
          <div className="sm:col-span-2">
            <label htmlFor={`${testidPrefix}-budget`} className={labelCls}>Monthly SEO Budget</label>
            <select id={`${testidPrefix}-budget`} data-testid={`${testidPrefix}-budget-select`} className={inputCls} value={form.budget} onChange={set("budget")}>
              <option value="">Select a range</option>
              <option>Under ₹5,000</option>
              <option>₹5,000 – ₹10,000</option>
              <option>₹10,000 – ₹20,000</option>
              <option>₹20,000+</option>
              <option>Not sure yet</option>
            </select>
          </div>
        )}
        <div className="sm:col-span-2">
          <label htmlFor={`${testidPrefix}-message`} className={labelCls}>What do you want to improve? *</label>
          <textarea id={`${testidPrefix}-message`} rows={4} data-testid={`${testidPrefix}-message-input`} className={inputCls} value={form.message} onChange={set("message")} placeholder="e.g. We get traffic but no enquiries / our store doesn't rank for product searches..." />
          {errors.message && <p className="mt-1 text-xs text-red-400" role="alert">{errors.message}</p>}
        </div>
      </div>
      <button type="submit" disabled={status === "sending"} data-testid={`${testidPrefix}-submit`}
        className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-rw-green px-8 py-3.5 text-sm font-semibold text-rw-bg transition-all duration-200 hover:bg-rw-green2 hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0">
        {status === "sending" ? <Loader2 size={18} className="animate-spin" /> : <Send size={16} />}
        {status === "sending" ? "Sending..." : "Request SEO Audit"}
      </button>
    </form>
  );
}
